import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { inventoryApi } from '../../api/inventory-api-instance';
import { ApiError } from '../../api/errors';
import { PartialFailureNotice } from '../../components/PartialFailureNotice';
import { SearchableSelect } from '../../components/SearchableSelect';
import {
  itemStatuses,
  type CreateItemInput,
  type ItemStatus,
  type Part,
  type Site,
} from '../../domain/inventory';
import { statusLabels } from '../../domain/status';
import { FULL_FLEET_ITEMS_QUERY_KEY } from '../dashboard/aggregations';
import { humanizeIdentifier, sortByDisplayLabel } from '../../lib/formatting';
import { siteDisplayName, siteLabel, siteSearchAliases } from '../../lib/site-label';
import { cleanItemId, itemIdProblem } from '../../domain/item-id';

interface BulkCreateDrawerProps {
  parts: Part[];
  sites: Site[];
  onClose: () => void;
  onCreated: (itemIds: string[]) => void;
}

interface SharedDraft {
  partId: string;
  siteId: string;
  locationSiteId: string;
  location: string;
  hardwareRevision: string;
  status: ItemStatus | '';
  notes: string;
  needsAttention: boolean;
  attentionReason: string;
}

const EMPTY_DRAFT: SharedDraft = {
  partId: '',
  siteId: '',
  locationSiteId: '',
  location: '',
  hardwareRevision: '',
  status: '',
  notes: '',
  needsAttention: false,
  attentionReason: '',
};
const alphabetizedStatuses = sortByDisplayLabel(itemStatuses, (status) => statusLabels[status]);

function parseSerials(value: string): string[] {
  return [
    ...new Set(
      value
        .split(/[\s,;]+/)
        .map(cleanItemId)
        .filter(Boolean),
    ),
  ];
}

function toInputs(serials: string[], draft: SharedDraft): CreateItemInput[] {
  return serials.map((id) => ({
    id,
    partId: draft.partId,
    siteId: draft.siteId,
    ...(draft.locationSiteId ? { locationSiteId: draft.locationSiteId } : {}),
    ...(draft.location.trim() ? { location: draft.location.trim() } : {}),
    ...(draft.hardwareRevision.trim() ? { hardwareRevision: draft.hardwareRevision.trim() } : {}),
    ...(draft.status ? { status: draft.status } : {}),
    ...(draft.notes.trim() ? { notes: draft.notes.trim() } : {}),
    needsAttention: draft.needsAttention,
    attentionReason: draft.needsAttention ? draft.attentionReason.trim() : '',
  }));
}

/**
 * Creates several serialized items in one atomic request, all sharing the
 * same part/site/status/notes -- the fields a batch of identical new devices
 * would have in common. See BatchUpdateDrawer for the same
 * atomic-batch-review shape applied to edits instead of creates.
 */
export function BulkCreateDrawer({ parts, sites, onClose, onCreated }: BulkCreateDrawerProps) {
  const queryClient = useQueryClient();
  const sortedParts = useMemo(
    () =>
      sortByDisplayLabel(
        parts,
        (part) => `${humanizeIdentifier(part.type)} ${humanizeIdentifier(part.build)}`,
      ),
    [parts],
  );
  const sortedSites = useMemo(
    () => sortByDisplayLabel(sites, (site) => siteDisplayName(site)),
    [sites],
  );
  const [serialsText, setSerialsText] = useState('');
  const [draft, setDraft] = useState<SharedDraft>(EMPTY_DRAFT);
  const [serials, setSerials] = useState<string[]>([]);
  const [reviewing, setReviewing] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [unknownOutcome, setUnknownOutcome] = useState(false);

  const mutation = useMutation({
    mutationFn: (inputs: CreateItemInput[]) => inventoryApi.batchCreateItems(inputs),
    onSuccess: async (created) => {
      // See EditItemDrawer's onSuccess for why the full-fleet query is
      // invalidated separately with refetchType: 'none'.
      await queryClient.invalidateQueries({ queryKey: ['items'] });
      void queryClient.invalidateQueries({
        queryKey: FULL_FLEET_ITEMS_QUERY_KEY,
        refetchType: 'none',
      });
      onCreated(created.map((item) => item.id));
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.code === 'NETWORK') {
        setUnknownOutcome(true);
        return;
      }
      setFormError(
        error instanceof Error ? error.message : 'The items could not be created; none were.',
      );
    },
  });

  const assignedSite = sites.find((site) => site.id === draft.siteId);
  const locationSite = sites.find((site) => site.id === (draft.locationSiteId || draft.siteId));
  const part = parts.find((candidate) => candidate.id === draft.partId);

  function update<K extends keyof SharedDraft>(key: K, value: SharedDraft[K]): void {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  function handleContinue(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setFormError(null);
    const parsed = parseSerials(serialsText);
    if (parsed.length === 0) {
      setFormError('Enter at least one serial number.');
      return;
    }
    const invalid = parsed.filter((id) => itemIdProblem(id) !== null);
    if (invalid.length > 0) {
      setFormError(
        `${invalid.map((id) => `“${id}”`).join(', ')} ${invalid.length === 1 ? 'is not a valid serial' : 'are not valid serials'}: use only letters, digits, “.”, “-” and “_”.`,
      );
      return;
    }
    if (!draft.partId || !draft.siteId) {
      setFormError('Part definition and assigned site are required.');
      return;
    }
    if (draft.needsAttention && !draft.attentionReason.trim()) {
      setFormError('Add a reason before marking these items as needing attention.');
      return;
    }
    setSerials(parsed);
    setReviewing(true);
  }

  return (
    <div className="drawer-overlay" role="presentation" onClick={onClose}>
      {reviewing ? (
        <div
          className="drawer drawer-wide"
          role="dialog"
          aria-label="Review bulk create"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="drawer-heading">
            <div>
              <span className="step-label">Step 2 of 2 · Atomic batch review</span>
              <h2>Create {serials.length.toLocaleString()} items</h2>
            </div>
            <button
              type="button"
              className="icon-button"
              onClick={onClose}
              aria-label="Close bulk create drawer"
            >
              ×
            </button>
          </div>

          {formError ? (
            <p role="alert" className="form-error">
              {formError}
            </p>
          ) : null}
          {unknownOutcome ? (
            <PartialFailureNotice
              onCheckCurrentState={() => {
                void queryClient.invalidateQueries({ queryKey: ['items'] });
                onClose();
              }}
            />
          ) : null}

          <dl className="review-grid">
            <dt>Part definition</dt>
            <dd>
              {part
                ? `${humanizeIdentifier(part.type)} · ${humanizeIdentifier(part.build)}`
                : draft.partId}
            </dd>
            <dt>Assigned site</dt>
            <dd>{siteLabel(assignedSite, draft.siteId)}</dd>
            <dt>Current location</dt>
            <dd>{siteLabel(locationSite, draft.locationSiteId || draft.siteId)}</dd>
            <dt>Hardware revision</dt>
            <dd>{draft.hardwareRevision.trim() || 'Not recorded'}</dd>
            <dt>Status</dt>
            <dd>
              {draft.status ? statusLabels[draft.status] : 'Not recorded (data-quality flag)'}
            </dd>
            <dt>Attention</dt>
            <dd>{draft.needsAttention ? draft.attentionReason.trim() : 'No attention flag'}</dd>
          </dl>

          <p className="batch-warning">
            Every item above is created with the same fields. If any serial fails validation or
            already exists, no items are created.
          </p>

          <ul className="batch-item-list">
            {serials.map((id) => (
              <li key={id} className="batch-item-row">
                <span className="mono">{id}</span>
              </li>
            ))}
          </ul>

          <div className="drawer-actions">
            <button type="button" onClick={() => setReviewing(false)} disabled={mutation.isPending}>
              Back
            </button>
            <button
              type="button"
              className="primary"
              onClick={() => {
                setFormError(null);
                setUnknownOutcome(false);
                mutation.mutate(toInputs(serials, draft));
              }}
              disabled={mutation.isPending}
            >
              {mutation.isPending
                ? 'Creating…'
                : `Create ${serials.length.toLocaleString()} items`}
            </button>
          </div>
        </div>
      ) : (
        <form
          className="drawer drawer-wide"
          aria-label="Bulk create items"
          onClick={(event) => event.stopPropagation()}
          onSubmit={handleContinue}
        >
          <div className="drawer-heading">
            <div>
              <span className="step-label">Step 1 of 2</span>
              <h2>Bulk create items</h2>
            </div>
            <button
              type="button"
              className="icon-button"
              onClick={onClose}
              aria-label="Close bulk create drawer"
            >
              ×
            </button>
          </div>
          <p className="drawer-version-note">
            Every serial below is created with the same part, site, and status.
          </p>

          {formError ? (
            <p role="alert" className="form-error">
              {formError}
            </p>
          ) : null}

          <label>
            Serial numbers
            <textarea
              value={serialsText}
              onChange={(event) => setSerialsText(event.target.value)}
              placeholder="Paste or type serial numbers, separated by commas, spaces, or new lines"
              rows={5}
              autoFocus
              required
            />
          </label>
          <p className="drawer-version-note">
            {parseSerials(serialsText).length.toLocaleString()} unique serial
            {parseSerials(serialsText).length === 1 ? '' : 's'} entered.
          </p>

          <label>
            Part definition
            <SearchableSelect
              ariaLabel="Part definition"
              onChange={(value) => update('partId', value)}
              options={[
                { value: '', label: 'Select a part…' },
                ...sortedParts.map((candidate) => ({
                  value: candidate.id,
                  label: `${humanizeIdentifier(candidate.type)} · ${humanizeIdentifier(candidate.build)}`,
                  aliases: [candidate.id, candidate.type, candidate.build],
                })),
              ]}
              placeholder="Select a part…"
              required
              value={draft.partId}
            />
          </label>
          <label>
            Assigned site
            <SearchableSelect
              ariaLabel="Assigned site"
              onChange={(value) => update('siteId', value)}
              options={[
                { value: '', label: 'Select an accountable site…' },
                ...sortedSites.map((site) => ({
                  value: site.id,
                  label: siteLabel(site),
                  aliases: siteSearchAliases(site),
                })),
              ]}
              placeholder="Select an accountable site…"
              required
              value={draft.siteId}
            />
          </label>

          <div className="form-divider">
            <span>Operational context</span>
          </div>

          <label>
            Current location site
            <SearchableSelect
              ariaLabel="Current location site"
              onChange={(value) => update('locationSiteId', value)}
              options={[
                { value: '', label: 'Same as assigned site' },
                ...sortedSites.map((site) => ({
                  value: site.id,
                  label: siteLabel(site),
                  aliases: siteSearchAliases(site),
                })),
              ]}
              placeholder="Same as assigned site"
              value={draft.locationSiteId}
            />
          </label>
          <label>
            Location detail
            <input
              value={draft.location}
              onChange={(event) => update('location', event.target.value)}
              placeholder="Zone, shelf, workbench, vessel…"
            />
          </label>
          <label>
            Hardware revision
            <input
              value={draft.hardwareRevision}
              onChange={(event) => update('hardwareRevision', event.target.value)}
            />
          </label>
          <label>
            Status
            <SearchableSelect
              ariaLabel="Status"
              onChange={(value) => update('status', value as ItemStatus | '')}
              options={[
                { value: '', label: 'Not recorded' },
                ...alphabetizedStatuses
                  .filter((value) => value !== 'STATUS_UNSPECIFIED')
                  .map((value) => ({ value, label: statusLabels[value], aliases: [value] })),
              ]}
              placeholder="Not recorded"
              value={draft.status}
            />
          </label>
          <label>
            Notes
            <textarea
              value={draft.notes}
              onChange={(event) => update('notes', event.target.value)}
              rows={3}
            />
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={draft.needsAttention}
              onChange={(event) => {
                update('needsAttention', event.target.checked);
                if (!event.target.checked) update('attentionReason', '');
              }}
            />
            Needs attention
          </label>
          {draft.needsAttention ? (
            <label>
              Shared attention reason
              <input
                value={draft.attentionReason}
                onChange={(event) => update('attentionReason', event.target.value)}
                required
              />
            </label>
          ) : null}

          <div className="drawer-actions">
            <button type="button" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="primary">
              Review items
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
